import store from '@/store';
import { CurrentLang } from 'utils/lang';
import AdyenWeb from '@adyen/adyen-web';
import availablePaymentEngines from '../constants/PaymentEngine';

// Native ESM (Vite) has no runtime `require`. Statically import the SDK and
// keep the original SSR guard: null on the server, the SDK in the browser.
const Adyen = typeof window === 'undefined' ? null : AdyenWeb;

let adyen = null;

export function getAdyen() {
  return adyen;
}

export function getAdyenInstance() {
  if (adyen) {
    const instances = adyen.components;
    const lastInstance = instances[instances.length - 1];
    const validInstances = instances.filter((instance) => instance.isValid);

    if (validInstances.length > 0) {
      const lastValidInstance = validInstances[validInstances.length - 1];

      lastInstance.setState(lastValidInstance.state);
      return lastInstance;
    }

    return lastInstance;
  }
}

export function removeAdyenInstance() {
  if (adyen) {
    if (adyen.components.length > 0) {
      adyen.components[adyen.components.length - 1].remove();
    }
  }
}

export function setupAdyen(whitelabelEnvs) {
  const { enabled, environment, publicKey: clientKey } = whitelabelEnvs.adyen || {};

  if (!enabled) return;
  if (!Adyen) return;

  adyen = new Adyen({
    locale: CurrentLang() || whitelabelEnvs.lang.default,
    clientKey,
    environment,
  });
}

export function shouldUseAdyen(paymentEngine) {
  const adyenIsEnabled = store.getState().whitelabelConfig.env.adyen.enabled;
  return adyenIsEnabled && paymentEngine === availablePaymentEngines.adyen;
}

/**
 * Check if Adyen Apple Pay is enabled in whitelabel configuration.
 * @returns {boolean} True if Adyen Apple Pay is enabled
 */
export function isAdyenApplePayEnabled() {
  const config = store.getState().whitelabelConfig.env;
  return Boolean(config?.adyen?.applePay?.enabled);
}

/**
 * Get Adyen Apple Pay configuration from whitelabel config.
 * @returns {Object|null} Adyen Apple Pay configuration or null if not available
 */
export function getAdyenApplePayConfig() {
  if (!isAdyenApplePayEnabled()) {
    return null;
  }

  const config = store.getState().whitelabelConfig.env;
  return config.adyen.applePay;
}

/**
 * Check if Adyen Google Pay is enabled in whitelabel configuration.
 * @returns {boolean} True if Adyen Google Pay is enabled
 */
export function isAdyenGooglePayEnabled() {
  const config = store.getState().whitelabelConfig.env;
  return Boolean(config?.adyen?.googlePay?.enabled);
}

/**
 * Get Adyen Google Pay configuration from whitelabel config.
 * @returns {Object|null} Adyen Google Pay configuration or null if not available
 */
export function getAdyenGooglePayConfig() {
  if (!isAdyenGooglePayEnabled()) {
    return null;
  }

  const config = store.getState().whitelabelConfig.env;
  return config.adyen.googlePay;
}

/**
 * Get the Adyen test acquirer response code from URL query parameters.
 * Used to trigger specific refusal reasons in the Adyen test environment
 * via additionalData.RequestedTestAcquirerResponseCode.
 * @returns {string|null} The test response code or null if not set
 */
export function getAdyenTestResponseCode() {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  return params.get('adyenTestCode');
}
